// Live-relayed drawing surface for the artist assigned to a drawing window.
// Streams every canvas action to the room and restores the server-accumulated
// canvas after a refresh/rejoin. Spectators mirror it with DrawingMirror.

import React from 'react';
import { DrawingCanvas } from './DrawingCanvas';
import { useDrawArtistRelay, useDrawArtistSnapshot } from './useDrawRelay';
import { useRoomConfig } from '@/hooks';

export interface LiveDrawingCanvasProps {
  /** The open interaction window the drawing streams under */
  windowId: string;
  /** The artist's own player id (relay canvases are per-artist) */
  playerId: string;
  /** Must match every mirror and the backend rasterizer for this game */
  aspectRatio: number;
  className?: string;
  toolbarFrameClassName?: string;
  toolbarBefore?: React.ReactNode;
  toolbarAfter?: React.ReactNode;
  canvasFrameClassName?: string;
  canvasSlotClassName?: string;
  canvasClassName?: string;
  canvasBefore?: React.ReactNode;
  canvasAfter?: React.ReactNode;
}

const LiveDrawingCanvas: React.FC<LiveDrawingCanvasProps> = ({
  windowId,
  playerId,
  aspectRatio,
  className,
  toolbarFrameClassName,
  toolbarBefore,
  toolbarAfter,
  canvasFrameClassName,
  canvasSlotClassName,
  canvasClassName,
  canvasBefore,
  canvasAfter,
}) => {
  const { roomCode } = useRoomConfig();
  const target = { roomCode: roomCode ?? '', windowId, playerId };
  const emit = useDrawArtistRelay(target);
  const snapshot = useDrawArtistSnapshot(target);

  return (
    <DrawingCanvas
      aspectRatio={aspectRatio}
      onAction={emit}
      hydrateSnapshot={snapshot}
      className={className}
      toolbarFrameClassName={toolbarFrameClassName}
      toolbarBefore={toolbarBefore}
      toolbarAfter={toolbarAfter}
      canvasFrameClassName={canvasFrameClassName}
      canvasSlotClassName={canvasSlotClassName}
      canvasClassName={canvasClassName}
      canvasBefore={canvasBefore}
      canvasAfter={canvasAfter}
    />
  );
};

export default LiveDrawingCanvas;
